/**
 * The Table and Charts tabs of a KPI dashboard: the grid, the charts drawn from
 * the same numbers, and everything that acts on them.
 *
 * One component for both tabs (DOP-609) because they are one scope read two
 * ways: the property chips and the period switch above them, the calculated
 * rows, the Empty rows choice all hold across the switch, and the table the
 * charts plot is the table the grid shows.
 *
 * Lifted out of `pages/KpiDashboard.tsx` by DOP-479, unchanged in behaviour.
 * The page above it now owns what the SHELL owns — the title, Run now,
 * Download, the Analyst rail and the tab the URL names — and what the rail's
 * descriptor has to read (the scope and the selected cell). What is left here
 * is the grid and the controls that only mean something next to it.
 *
 * Calculated rows live here rather than on the page for the same reason: they
 * are a property of the grid, and nothing outside the Data tab can see them.
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";

import ErrorDisplay from "../ErrorDisplay";
import NoDataState from "../PortfolioManagement/NoDataState";
import type { PageTab } from "../Dashboards/PageTabs";
import KpiAddMetricDialog from "./KpiAddMetricDialog";
import KpiCellGuide from "./KpiCellGuide";
import KpiChartCard from "./KpiChartCard";
import KpiDataToolbar from "./KpiDataToolbar";
import KpiDrillIn from "./KpiDrillIn";
import KpiGrainToggle from "./KpiGrainToggle";
import KpiGrid, { type KpiCellCoords } from "./KpiGrid";
import KpiPropertyChips from "./KpiPropertyChips";
import KpiTour from "./KpiTour";
import KpiMixCard from "./KpiMixCard";
import { kpiCharts } from "./kpiCharts";
import { emptyRowReasons, hideableEmptyRowLabels } from "./kpiEmptyRow";
import type { KpiMetricDrop } from "./kpiLayout";
import type { KpiLayoutState } from "../../hooks/useKpiLayout";
import type { KpiOnboardingState } from "../../hooks/useKpiOnboarding";
import { useKpiCustomMetrics } from "./useKpiCustomMetrics";
import type { KpiTableData } from "./useKpiTable";
import type { KpiCustomMetricInput } from "../../services/kpiCustomMetricsService";
import { type KpiPeriodGrain, isSupportedPeriodGrain } from "../../types/kpi";

/** Named by the grain that came BACK — that is what the columns are. */
const GRAIN_NOTE: Record<KpiPeriodGrain, string> = {
	month: "Showing monthly periods — no aggregated buckets came back for this scope.",
	quarter: "Showing quarterly periods — the requested grain was not available.",
	year: "Showing yearly periods — the requested grain was not available.",
};

const EMPTY_MESSAGE =
	"No KPI values yet — run this workflow and they will appear here.";

/**
 * The account has no KPI scope at all (the server answered 403, not a failure).
 * Reads as an empty dashboard rather than a broken one — no Retry, because the
 * request that produced this can only produce it again.
 */
const UNAVAILABLE_MESSAGE = "Workflow is Empty";

/**
 * The empty state at an AGGREGATE grain is a different fact from the empty
 * state at month: it usually means this scope has no buckets of that size, not
 * that there is nothing extracted — so it points at the control that can
 * answer that rather than at a run the user may have already made.
 */
const EMPTY_MESSAGE_BY_GRAIN: Record<KpiPeriodGrain, string> = {
	month: EMPTY_MESSAGE,
	quarter:
		"No KPI values in quarterly periods for this scope — try Monthly, or run this workflow.",
	year: "No KPI values in yearly periods for this scope — try Monthly, or run this workflow.",
};

export type KpiDataView = "table" | "charts";

export interface KpiDataBodyProps {
	/** Which tab this is: the grid, or the same numbers drawn over time. */
	view: KpiDataView;
	data: KpiTableData;
	/** Portfolio first, then the properties this dashboard actually has data for. */
	propertyTabs: readonly PageTab[];
	activeProperty: string;
	onSelectProperty: (id: string) => void;
	grain: KpiPeriodGrain;
	onGrainChange: (grain: KpiPeriodGrain) => void;
	hideEmptyRows: boolean;
	onHideEmptyRowsChange: (hide: boolean) => void;
	/** The cell key, the tour, and whether each has been seen. */
	onboarding: KpiOnboardingState;
	/** The row order: Summit's default or this user's own (DOP-613). */
	layout: KpiLayoutState;
	/** The cell whose evidence is open IN the grid — the primary interaction. */
	expanded: KpiCellCoords | null;
	onExpandCell: (cell: KpiCellCoords | null) => void;
	/** The cell whose metadata/override panel is open, reached from that. */
	detailsFor: KpiCellCoords | null;
	onOpenDetails: (cell: KpiCellCoords | null) => void;
	/** "Ask about this" from the drill-in. Absent when there is no Analyst. */
	onAsk?: (question: string) => void;
}

const KpiDataBody: React.FC<KpiDataBodyProps> = ({
	view,
	data,
	propertyTabs,
	activeProperty,
	onSelectProperty,
	grain,
	onGrainChange,
	hideEmptyRows,
	onHideEmptyRowsChange,
	onboarding,
	layout,
	expanded,
	onExpandCell,
	detailsFor,
	onOpenDetails,
	onAsk,
}) => {
	const { table, loading, error, unavailable, refreshing, stale, refresh } = data;
	const [addingMetric, setAddingMetric] = useState(false);
	// The custom row whose definition is open in the dialog, by key. Null means
	// the dialog, if open, is defining a new row.
	const [editingMetricKey, setEditingMetricKey] = useState<string | null>(null);
	const [deletingMetricKey, setDeletingMetricKey] = useState<string | null>(null);
	// A delete that did not happen has to say so. The row is still on screen
	// either way, so silence would read as "removed" for a row that was not.
	const [deleteError, setDeleteError] = useState<string | null>(null);

	const customMetrics = useKpiCustomMetrics();

	const handleCreateMetric = useCallback(
		async (input: KpiCustomMetricInput) => {
			const outcome = await customMetrics.create(input);
			// The new row only exists on screen once the table carries it — the
			// server computes its values. A FAILED create refetches too: it may
			// have committed before the response was lost.
			if (outcome.kind !== "invalid") refresh();
			return outcome;
		},
		[customMetrics, refresh],
	);

	const editingMetric = useMemo(
		() =>
			customMetrics.metrics.find(
				(candidate) => candidate.key === editingMetricKey,
			) ?? null,
		[customMetrics.metrics, editingMetricKey],
	);

	const handleUpdateMetric = useCallback(
		async (input: KpiCustomMetricInput) => {
			if (!editingMetric) return { kind: "failed" as const };
			const outcome = await customMetrics.update(editingMetric.id, input);
			// The row's VALUES are the server's — a changed expression recomputes
			// them — so the grid has to come back from the API either way.
			if (outcome.kind !== "invalid") refresh();
			return outcome;
		},
		[customMetrics, editingMetric, refresh],
	);

	const handleDeleteCustomMetric = useCallback(
		async (metricKey: string) => {
			const id = customMetrics.idForKey(metricKey);
			if (!id) return;
			setDeletingMetricKey(metricKey);
			setDeleteError(null);
			const outcome = await customMetrics.remove(id);
			setDeletingMetricKey(null);
			// Refetch either way: a DELETE whose response was lost may still have
			// committed, so the grid is where the user finds out. A failure says
			// only that it could not be CONFIRMED.
			refresh();
			if (outcome.kind !== "deleted") {
				setDeleteError(
					"Could not confirm that row was removed — the grid has been reloaded.",
				);
			}
		},
		[customMetrics, refresh],
	);

	/**
	 * Offered only while the definitions are known: the delete addresses an id
	 * the table payload does not carry, so without the list there is nothing a
	 * click could do — and an affordance that cannot act is worse than none.
	 */
	const deleteHandler = useMemo(
		() =>
			customMetrics.metrics.length > 0
				? (metricKey: string) => void handleDeleteCustomMetric(metricKey)
				: undefined,
		[customMetrics.metrics.length, handleDeleteCustomMetric],
	);

	/** Same gate as the delete: an edit addresses an id only the list knows. */
	const editHandler =
		customMetrics.metrics.length > 0
			? (metricKey: string) => setEditingMetricKey(metricKey)
			: undefined;

	// The grain the numbers on screen were aggregated into — the server's
	// answer, not the pending request. Labels follow the data, so a refetch in
	// flight never relabels a grid it has not replaced yet.
	const tableGrain = table?.period_grain ?? "month";
	/**
	 * The server aggregated into a bucket this build cannot label. Version skew,
	 * not corruption: the columns fall back to raw period keys and the notice
	 * says which grain they are in, by the server's own name for it.
	 */
	const unsupportedGrain = table !== null && !isSupportedPeriodGrain(tableGrain);
	/**
	 * The server answered in a different bucket than was asked for. Saying so is
	 * the difference between a control that looks broken and one that reports
	 * what it got.
	 */
	const grainMismatch =
		table !== null && !refreshing && !unsupportedGrain && tableGrain !== grain;

	const detailsMetric = useMemo(() => {
		if (!table || !detailsFor) return null;
		for (const group of table.groups) {
			const metric = group.metrics.find((m) => m.key === detailsFor.metricKey);
			if (metric) return metric;
		}
		return null;
	}, [table, detailsFor]);

	// The table in the dashboard's order, with the dashboard's names — what the
	// grid, the charts and the empty-row count all read (DOP-613).
	const { arrange } = layout;
	const arranged = useMemo(() => (table ? arrange(table) : table), [table, arrange]);

	// The fixed set of charts (see `kpiCharts.ts`), from the table as arranged.
	// Rows with no numbers are never drawn, whatever the Empty rows switch says.
	const charts = useMemo(() => (arranged ? kpiCharts(arranged) : []), [arranged]);

	/** The rows the Empty rows switch acts on — counted, and named when hidden. */
	const emptyRowLabels = useMemo(
		() => (arranged ? hideableEmptyRowLabels(arranged.groups) : []),
		[arranged],
	);

	// The grid shows one generic line per empty row; the specific reason for
	// each goes here, once per table, for whoever is supporting the customer.
	useEffect(() => {
		if (!arranged) return;
		const reasons = emptyRowReasons(arranged.groups);
		if (reasons.length > 0) console.info("[kpi] empty rows", reasons);
	}, [arranged]);

	const { moveMetric, moveSection } = layout;
	const handleMoveMetric = useCallback(
		(key: string, drop: KpiMetricDrop) => {
			if (table) moveMetric(table, key, drop);
		},
		[table, moveMetric],
	);
	const handleMoveSection = useCallback(
		(key: string, drop: { beforeKey?: string | null; afterKey?: string | null }) => {
			if (table) moveSection(table, key, drop);
		},
		[table, moveSection],
	);


	// The tour runs once, the first time a table with numbers is on screen.
	// Not before sign-in resolves: until then the "seen" flag belongs to nobody.
	const { ready: onboardingReady, tourSeen, tourOpen, startTour, endTour } =
		onboarding;
	// "With data" means at least one number on the grid — period buckets over
	// all-empty cells are an empty dashboard with nothing to point at.
	const hasValues = useMemo(
		() =>
			(table?.groups ?? []).some((group) =>
				group.metrics.some((metric) =>
					Object.values(metric.cells).some(
						(cell) => cell?.effective_value != null,
					),
				),
			),
		[table],
	);
	useEffect(() => {
		if (
			view === "table" &&
			onboardingReady &&
			hasValues &&
			!loading &&
			!tourSeen &&
			!tourOpen
		) {
			startTour();
		}
	}, [view, onboardingReady, hasValues, loading, tourSeen, tourOpen, startTour]);

	/**
	 * The shell's body is a padded, scrollable block; the grid needs a bounded
	 * column it can scroll INSIDE, which is what `h-full` gives it.
	 *
	 * The property chips and the period switch head both tabs, and every state
	 * of them — a grain with no buckets must not remove the only control that
	 * can leave it. This column must NOT clip: the clipping the grid needs
	 * belongs to the grid's own region, and the shell's body backstops the rest.
	 */
	const shellBody = (children: React.ReactNode) => (
		<div className="flex h-full min-h-0 min-w-0 flex-col gap-3">
			<div className="flex shrink-0 items-center gap-4">
				<KpiPropertyChips
					options={propertyTabs}
					activeId={activeProperty}
					onSelect={onSelectProperty}
				/>
				<div className="ml-auto shrink-0">
					<KpiGrainToggle
						grain={grain}
						onChange={onGrainChange}
						disabled={refreshing}
					/>
				</div>
			</div>
			{children}
		</div>
	);

	if (error && table === null) {
		return shellBody(<ErrorDisplay error={error} onRetry={refresh} />);
	}
	if (unavailable && !loading) {
		// Nothing to read for this account — an empty dashboard, not a broken
		// one. No Refresh: the request that produced this can only produce it
		// again until the scope itself changes.
		return shellBody(<NoDataState message={UNAVAILABLE_MESSAGE} />);
	}
	if (loading && table === null) {
		return shellBody(
			<div className="py-6">
				<div className="h-[420px] animate-pulse rounded-lg border border-gray-200 bg-white" />
			</div>,
		);
	}
	if (table === null || table.periods.length === 0) {
		// The grain toggle stays on screen: a grain with no buckets otherwise
		// removes the only control that can leave it.
		return shellBody(
			<NoDataState message={EMPTY_MESSAGE_BY_GRAIN[grain]} onRefresh={refresh} />,
		);
	}

	return shellBody(
		<>
			<div className="flex min-h-0 min-w-0 flex-1 overflow-hidden">
				<div className="flex min-h-0 min-w-0 flex-1 flex-col gap-3">
					{/* A reload that failed leaves the PREVIOUS table on screen.
					    Rendering it without saying so presents numbers a mutation
					    has already moved as current ones. */}
					{error && (
						<p role="alert" className="text-sm text-red-600">
							Could not reload the KPI table — these numbers may be out of
							date.{" "}
							<button
								type="button"
								onClick={refresh}
								className="underline hover:no-underline"
							>
								Try again
							</button>
						</p>
					)}

					{unsupportedGrain && (
						<p className="text-sm text-text-muted">
							This view does not know the “{tableGrain}” period grain the
							server returned — columns are shown as raw period dates.
						</p>
					)}

					{grainMismatch && isSupportedPeriodGrain(tableGrain) && (
						<p className="text-sm text-text-muted">{GRAIN_NOTE[tableGrain]}</p>
					)}

					{deleteError && (
						<p role="alert" className="text-sm text-red-600">
							{deleteError}
						</p>
					)}

					{view === "table" ? (
						<>
							<KpiDataToolbar
								refreshing={refreshing}
								onRefresh={refresh}
								hideEmptyRows={hideEmptyRows}
								onHideEmptyRowsChange={onHideEmptyRowsChange}
								emptyRowCount={emptyRowLabels.length}
								onAddMetric={() => setAddingMetric(true)}
								onStartTour={startTour}
								orderCustomized={layout.customized}
								onResetOrder={layout.reset}
								orderSaveFailed={layout.saveFailed}
							/>
							{onboarding.guideVisible && (
								<KpiCellGuide onClose={onboarding.hideGuide} />
							)}
							{/* The grid is HELD, not blanked, while another grain or
							    property loads: unmounting it reads as a page reload.
							    Dimmed and inert for as long as it is the previous
							    scope's answer. */}
							<div
								data-tour="kpi-grid"
								className={`flex min-h-0 min-w-0 flex-1 flex-col transition-opacity duration-150 ${
									stale ? "pointer-events-none opacity-40" : ""
								}`}
								aria-busy={stale || undefined}
							>
								<KpiGrid
									table={arranged ?? table}
									expanded={expanded}
									onExpandCell={onExpandCell}
									onOpenDetails={onOpenDetails}
									onOverride={data.override}
									onRevert={data.revert}
									onDeleteCustomMetric={deleteHandler}
									onEditCustomMetric={editHandler}
									deletingMetricKey={deletingMetricKey}
									hideEmptyRows={hideEmptyRows}
									onMoveMetric={handleMoveMetric}
									onMoveSection={handleMoveSection}
								/>
							</div>
						</>
					) : (
						<div
							className={`min-h-0 min-w-0 flex-1 overflow-y-auto transition-opacity duration-150 ${
								stale ? "pointer-events-none opacity-40" : ""
							}`}
							aria-busy={stale || undefined}
						>
							<p className="mb-3 text-sm text-text-muted">
								These charts use the same numbers as the Table. Hover a point
								for its value; open the cell on the Table to see its evidence.
							</p>
							<div className="grid grid-cols-1 gap-3.5 xl:grid-cols-2">
								{charts.map((chart) =>
									chart.kind === "mix" ? (
										<KpiMixCard
											key={chart.key}
											label={chart.label}
											mix={chart.mix}
											grain={tableGrain}
										/>
									) : (
										<KpiChartCard
											key={chart.group.key}
											group={chart.group}
											periods={table.periods}
											grain={tableGrain}
											mixedUnits={chart.mixedUnits}
										/>
									),
								)}
							</div>
						</div>
					)}
				</div>

				{detailsFor && detailsMetric && (
					<KpiDrillIn
						propertyKey={table.property_key}
						metric={detailsMetric}
						period={detailsFor.period}
						grain={tableGrain}
						cell={detailsMetric.cells[detailsFor.period]}
						onClose={() => onOpenDetails(null)}
						onOverride={data.override}
						onRevert={data.revert}
						onAsk={onAsk}
					/>
				)}
			</div>

			{addingMetric && (
				<KpiAddMetricDialog
					groups={table.groups}
					onSubmit={handleCreateMetric}
					onClose={() => setAddingMetric(false)}
				/>
			)}

			{tourOpen && view === "table" && <KpiTour onClose={endTour} />}

			{editingMetric && (
				// Keyed by id so switching rows remounts the dialog: its fields are
				// seeded once, and a shared instance would keep the first row's text
				// under the second row's heading.
				<KpiAddMetricDialog
					key={editingMetric.id}
					groups={table.groups}
					metric={editingMetric}
					onSubmit={handleUpdateMetric}
					onClose={() => setEditingMetricKey(null)}
				/>
			)}
		</>,
	);
};

export default KpiDataBody;
